import React, { FC, useContext, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { compact } from 'lodash';
import { AppDispatch } from '@typings/redux';
import { Box } from '@mui/material';
import { limits } from 'config/pagination';
import { LocaleContext } from 'context/locale';
import { fetchPersonalDataAccessList } from 'store/reports/reports.action';
import { personalDataAccessListSelector } from 'store/reports/reports.selector';
import { clearPersonalDataAccess } from 'store/reports/reports.action';
import { Paper } from 'components/shared/surfaces/Paper/Paper';
import { ColumnsConfig } from 'components/shared/data/DataGrid/DataGrid.types';
import { DataGrid } from 'components/shared/data/DataGrid/DataGrid';
import { PersonalDataAccessModel, UserRole, Action, BrowserPrinterListActionHandlers } from '../AccessList.types';
import { PrintedStudentsModal } from './PrintedStudentsModal';

export const BrowserPrinterList: FC = () => {
  const { trans } = useContext(LocaleContext);
  const dispatch = useDispatch<AppDispatch>();
  const { list, total } = useSelector(personalDataAccessListSelector);
  const [showStudents, setShowStudents] = useState(false);
  const [studentId, setStudentId] = useState<string>();

  useEffect(() => {
    dispatch(clearPersonalDataAccess());
  }, [dispatch]);

  const actionHandlers: BrowserPrinterListActionHandlers = {
    details: (id: string) => {
      setStudentId(id);
      setShowStudents(true);
    },
  };

  const getUserRole = (name: UserRole) => {
    if (name === UserRole.ROLE_ADMIN) {
      return trans('PERSONAL_DATA_ACCESS.LIST.COLUMNS.USER_ROLE.ROLE_ADMIN');
    } else if (name === UserRole.ROLE_USER) {
      return trans('PERSONAL_DATA_ACCESS.LIST.COLUMNS.USER_ROLE.ROLE_USER');
    } else {
      return '';
    }
  };

  const getAccessType = (values: PersonalDataAccessModel) => {
    if (values.action === Action.PRINTOUT) {
      return `${trans('PERSONAL_DATA_ACCESS.LIST.COLUMNS.ACTION_DESCRIPTION.PRINTOUT')}${values.url}`;
    } else {
      return values.description;
    }
  };

  const getColumns: ColumnsConfig<PersonalDataAccessModel, void> = () => [
    {
      field: 'accessDate',
      name: 'PERSONAL_DATA_ACCESS.LIST.COLUMNS.ACCESS_DATE',
      columnParams: { minWidth: 130 },
      format: (data, trans, formatter) => {
        return formatter.formatDateWithTime(data.accessDate);
      },
    },
    {
      field: 'userRole',
      name: 'PERSONAL_DATA_ACCESS.LIST.COLUMNS.USER_ROLE',
      format: (data) => getUserRole(data.userRole),
    },
    {
      field: 'userName',
      name: 'PERSONAL_DATA_ACCESS.LIST.COLUMNS.USER_NAME',
      format: (data) => data.userName,
    },
    {
      field: 'userIp',
      name: 'PERSONAL_DATA_ACCESS.LIST.COLUMNS.USER_IP',
      columnParams: { minWidth: 110 },
      format: (data) => data.userIp,
    },
    {
      field: 'actionDescription',
      name: 'PERSONAL_DATA_ACCESS.LIST.COLUMNS.ACTION_DESCRIPTION',
      columnParams: { flex: 1, minWidth: 280 },
      format: (data) => getAccessType(data),
    },
    {
      name: 'PERSONAL_DATA_ACCESS.LIST.COLUMNS.ACTIONS',
      cellClass: 'actions-cell',
      field: 'id',
      cellTooltip: () => '',
      renderer: 'buttonsCell',
      columnParams: { minWidth: 220 },
      rendererParams: {
        buttons: (item: PersonalDataAccessModel) => {
          return compact([
            {
              type: 'default',
              size: 'small',
              variant: 'outlined',
              label: 'COMMON.DISPLAY',
              icon: 'eye',
              tooltip: 'PERSONAL_DATA_ACCESS.LIST.COLUMNS.ACTIONS.SELECT',
              action: () => actionHandlers.details(item.id),
              disabled: item.action === Action.PRINTOUT || item.action === Action.OTHER_COMMUNE_STUDENT_LIST,
            },
          ]);
        },
      },
    },
  ];

  const onDataFetch = useCallback((page) => dispatch(fetchPersonalDataAccessList(page)), [dispatch]);
  const onDataProvide = useCallback((page) => list[page], [list]);

  return (
    <Paper innerSpacing={[4, 2]} square={true}>
      <Box sx={{ height: 500 }}>
        <DataGrid<PersonalDataAccessModel>
          columns={getColumns()}
          onDataProvide={onDataProvide}
          onDataFetch={onDataFetch}
          total={total}
          limit={limits.personalDataAccessList}
        />
      </Box>
      <PrintedStudentsModal id={studentId} open={showStudents} handleClose={() => setShowStudents(false)} />
    </Paper>
  );
};
